import classnames from 'classnames';
import { type CSSProperties, type ReactNode } from 'react';
import styled from 'styled-components';

import { Vertical } from '#src/atoms/layout/flex/index.tsx';

type ShieldShapeOrientation = 'up' | 'down';
type ShieldShapeSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const ORIENTATION_ROTATION: Record<ShieldShapeOrientation, number> = {
  up: 0,
  down: 180,
};

const SIZE_PX: Record<ShieldShapeSize, number> = {
  xxs: 32,
  xs: 44,
  sm: 54,
  md: 64,
  lg: 80,
  xl: 96,
};

const Root = styled.div`
  position: relative;
  flex-shrink: 0;
  width: var(--shape-size);
  height: var(--shape-size);
  transform: rotate(var(--rotation));
  background: var(--background);
  clip-path: polygon(
    57.36% 1%,
    99% 80.48%,
    89.85% 99%,
    10.07% 99%,
    1% 80.29%,
    42.44% 1%
  );
  &.shield-bottom-glow:after {
    position: absolute;
    inset: 0;
    z-index: -1;
    opacity: 0.4;
    content: '';
    transition: 0.3s ease opacity;
    background: radial-gradient(
      50% 50% at 50% 100%,
      var(--shadow, transparent),
      transparent 100%
    );
  }
  @supports (clip-path: shape(from 0% 0%, line to 100% 0%, close)) {
    /* stylelint-disable-next-line declaration-property-value-no-unknown */
    clip-path: shape(
      from 53.36% 1%,
      curve to 59.22% 4.54% with 57.36% 1%,
      line to 97.14% 76.94%,
      curve to 97.23% 84.07% with 99% 80.48%,
      line to 91.63% 95.41%,
      curve to 85.85% 99% with 89.85% 99%,
      line to 14.07% 99%,
      curve to 8.33% 95.4% with 10.07% 99%,
      line to 2.75% 83.89%,
      curve to 2.85% 76.75% with 1% 80.29%,
      line to 40.58% 4.55%,
      curve to 46.44% 1% with 42.44% 1%,
      close
    );
  }
`;

const Inner = styled.div`
  position: absolute;
  inset: 0;
  background: var(--border);
  clip-path: polygon(
    evenodd,
    0% 0%,
    100% 0%,
    100% 100%,
    0% 100%,
    0% 0%,
    calc(57.36% + var(--stroke-t) * -0.9%) calc(1% + var(--stroke-t) * 1.5%),
    calc(99% + var(--stroke-t) * -1.68%) calc(80.48% + var(--stroke-t) * 0.02%),
    calc(89.85% + var(--stroke-t) * -0.93%) calc(99% + var(--stroke-t) * -1.5%),
    calc(10.07% + var(--stroke-t) * 0.94%) calc(99% + var(--stroke-t) * -1.5%),
    calc(1% + var(--stroke-t) * 1.68%) calc(80.29% + var(--stroke-t) * 0.03%),
    calc(42.44% + var(--stroke-t) * 0.91%) calc(1% + var(--stroke-t) * 1.5%),
    calc(57.36% + var(--stroke-t) * -0.9%) calc(1% + var(--stroke-t) * 1.5%)
  );
  @supports (clip-path: shape(from 0% 0%, line to 100% 0%, close)) {
    /* stylelint-disable-next-line declaration-property-value-no-unknown */
    clip-path: shape(
      evenodd from 0% 0%,
      line to 100% 0%,
      line to 100% 100%,
      line to 0% 100%,
      close,
      move to 53.36% calc(1% + var(--stroke-t) * 1.5%),
      curve to calc(59.22% + var(--stroke-t) * -1.33%)
        calc(4.54% + var(--stroke-t) * 0.7%) with
        calc(57.36% + var(--stroke-t) * -0.9%) calc(1% + var(--stroke-t) * 1.5%),
      line to calc(97.14% + var(--stroke-t) * -1.32%)
        calc(76.94% + var(--stroke-t) * 0.69%),
      curve to calc(97.23% + var(--stroke-t) * -1.35%)
        calc(84.07% + var(--stroke-t) * -0.67%) with
        calc(99% + var(--stroke-t) * -1.68%)
        calc(80.48% + var(--stroke-t) * 0.02%),
      line to calc(91.63% + var(--stroke-t) * -1.35%)
        calc(95.41% + var(--stroke-t) * -0.66%),
      curve to 85.85% calc(99% + var(--stroke-t) * -1.5%) with
        calc(89.85% + var(--stroke-t) * -0.93%)
        calc(99% + var(--stroke-t) * -1.5%),
      line to 14.07% calc(99% + var(--stroke-t) * -1.5%),
      curve to calc(8.33% + var(--stroke-t) * 1.35%)
        calc(95.4% + var(--stroke-t) * -0.65%) with
        calc(10.07% + var(--stroke-t) * 0.94%)
        calc(99% + var(--stroke-t) * -1.5%),
      line to calc(2.75% + var(--stroke-t) * 1.35%)
        calc(83.89% + var(--stroke-t) * -0.65%),
      curve to calc(2.85% + var(--stroke-t) * 1.33%)
        calc(76.75% + var(--stroke-t) * 0.69%) with
        calc(1% + var(--stroke-t) * 1.68%)
        calc(80.29% + var(--stroke-t) * 0.03%),
      line to calc(40.58% + var(--stroke-t) * 1.33%)
        calc(4.55% + var(--stroke-t) * 0.69%),
      curve to 46.44% calc(1% + var(--stroke-t) * 1.5%) with
        calc(42.44% + var(--stroke-t) * 0.91%) calc(1% + var(--stroke-t) * 1.5%),
      close
    );
  }
`;

const IconWrapper = styled(Vertical).attrs({ center: true })`
  position: absolute;
  justify-content: center;
  transform: rotate(var(--rotation));
`;

type Props = {
  borderColor?: string;
  shadowColor?: string;
  background?: string;
  icon?: ReactNode;
  orientation?: ShieldShapeOrientation;
  size?: ShieldShapeSize;
  /** Multiplier applied to the default border/stroke thickness. 1 = default, 2 = twice as thick, 0 = no visible border. */
  borderWidth?: number;
  className?: string;
  style?: CSSProperties;
};

export const ShieldShape = ({
  borderColor = 'linear-gradient(to bottom, var(--c-nd-150) 0, var(--c-nd-300) 30%, var(--c-nd-600) 100%)',
  shadowColor = 'transparent',
  background = 'var(--c-nl-800)',
  icon,
  orientation = 'down',
  size = 'md',
  borderWidth = 2,
  className,
  style,
}: Props) => {
  const rotation = ORIENTATION_ROTATION[orientation];
  const sizePx = SIZE_PX[size];

  return (
    <Root
      className={classnames(className, {
        'shield-bottom-glow': !!shadowColor,
      })}
      style={
        {
          '--rotation': `${rotation}deg`,
          '--shape-size': `${sizePx}px`,
          '--background': background,
          '--border': borderColor,
          '--shadow': shadowColor,
          '--stroke-t': borderWidth,
          ...style,
        } as CSSProperties
      }
    >
      <Inner />
      {!!icon && (
        <IconWrapper
          style={
            {
              top: '15%',
              bottom: 0,
              left: 0,
              right: 0,
              '--rotation': `${-rotation}deg`,
            } as CSSProperties
          }
        >
          {icon}
        </IconWrapper>
      )}
    </Root>
  );
};
